// @ts-strict-ignore
import { Box } from '@usealloy/component-library';
import React from 'react';

import { selectWorkflowData } from 'flow/store/redux/selectors';
import type {
  DataRoute,
  MatrixModel as MatrixModelType,
} from 'flow/store/engine/types';

import { useAppSelector } from '~/store/reduxHooks';

import {
  getDataRouteTarget, buildReadOnlyMatrix, getMatrixColumns, getMatrixRows,
} from './helpers';
import { isMatrix1Dimensional } from '~/dashboard/views/workflows/service/actions';


interface MatrixModelProps {
  matrixModel: MatrixModelType;
}

function ReadOnlyMatrixModel({ matrixModel }: MatrixModelProps): JSX.Element {
  const workflowData = useAppSelector(selectWorkflowData);
  const { actions } = workflowData;

  const is1Dimensional = isMatrix1Dimensional(matrixModel.axis.x);

  const generateDataRouteString = React.useCallback((dataRoute: DataRoute) => {
    if (dataRoute) {
      return getDataRouteTarget({
        actions,
        dataRoute,
      });
    }
    return 'No Attribute Selected';
  }, [actions]);
  const dataRouteX = workflowData.dataRoutes[matrixModel.dataRouteX];
  const dataRouteY = workflowData.dataRoutes[matrixModel.dataRouteY];

  const matrixColumns = React.useMemo(() => getMatrixColumns({
    xAxisHeader: matrixModel.axis.x.axisHeader,
    xAxisHeaderDataType: matrixModel.axis.x.axisHeaderDataType,
    yDataRouteText: generateDataRouteString(dataRouteY),
  }), [dataRouteY, generateDataRouteString, matrixModel.axis.x]);

  const matrixRows = React.useMemo(() => getMatrixRows({
    matrix: matrixModel.matrix,
    yAxisHeader: matrixModel.axis.y.axisHeader,
    yAxisHeaderDataType: matrixModel.axis.y.axisHeaderDataType,
  }), [matrixModel.axis.y, matrixModel.matrix]);

  const MatrixTable = React.useMemo(() => buildReadOnlyMatrix({
    is1Dimensional,
    xDataRouteText: generateDataRouteString(dataRouteX),
    columns: matrixColumns,
    rows: matrixRows,
  }), [dataRouteX, generateDataRouteString, is1Dimensional, matrixColumns, matrixRows]);

  return (
    <Box
      justifyContent="flex-start"
      style={{
        paddingLeft: 'var(--cl-space-3',
        paddingTop: is1Dimensional ? 'var(--cl-space-5)' : 'var(--cl-space-3',
      }}
    >
      {MatrixTable}
    </Box>
  );
}

export default ReadOnlyMatrixModel;
